import React from 'react';
import PropTypes from 'prop-types';
import ProfileField from 'components/ProfileField';
import IconInput from 'components/IconInput';
import IconSelect from 'components/IconSelect';
import Button from 'components/Button';
import { Form, Title, AccountGrid, AccountInputs, ProfileContainer, useStyles } from './styles';
import useStudyFields from 'hooks/useStudyFields';
import useRoles from 'hooks/useRoles';
import ProfileAvatar from 'components/ProfileAvatar';
import { genders } from 'utils/profile';
import WhatsappPhoneTextField from 'components/WhatsappPhoneTextField';
import { Box } from '@mui/material';

const Props = {
  image: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  institution: PropTypes.string.isRequired,
  role: PropTypes.string.isRequired,
  fieldOfStudy: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  gender: PropTypes.string,
  whatsappNumber: PropTypes.string,
  isLoadingProfile: PropTypes.bool,
};

export default function GeneralForm({
  image,
  name,
  email,
  institution,
  role,
  fieldOfStudy,
  onChange,
  onSave,
  isLoading,
  gender,
  whatsappNumber,
}) {
  const studyFields = useStudyFields();
  const roles = useRoles();
  const classes = useStyles();

  return (
    <Form>
      <Title>Account Information</Title>

      <AccountGrid>
        <ProfileAvatar src={image} size="lg" margin="0 40px 0 0" />

        <AccountInputs>
          <IconInput
            icon="person"
            name="name"
            label="My Name"
            id="name"
            variant="outlined"
            type="text"
            value={name}
            onChange={(event) => onChange(event.target.value, event.target.name)}
            marginBottom={15}
            error={name.length < 2}
            helperText={name.length < 2 ? 'Name is required' : ''}
          />

          <IconSelect
            value={gender}
            marginBottom={30}
            label="Gender"
            icon="wc"
            name="gender"
            onChange={(event) => onChange(event.target.value, event.target.name)}
          >
            {genders.map(({ gender, id, value }) => (
              <option key={id} value={value}>
                {gender}
              </option>
            ))}
          </IconSelect>

          <Box mb={3} className={classes.whatsappNumber}>
            <WhatsappPhoneTextField
              variant="outlined"
              name="whatsappNumber"
              label="Phone"
              marginRightProp={0}
              showInformation={false}
              onChange={(value) => onChange(value, 'whatsappNumber')}
              value={whatsappNumber}
              error={whatsappNumber && whatsappNumber.length < 8}
              helperText={
                whatsappNumber && whatsappNumber.length < 8 ? 'Phone number is required' : ''
              }
            />
          </Box>

          <ProfileField label="E-mail" icon="email" value={email} />
        </AccountInputs>
      </AccountGrid>

      <Title>My Profile</Title>

      <ProfileContainer>
        <IconInput
          icon="apartment"
          name="institution"
          label="Institution"
          id="institution"
          variant="outlined"
          type="text"
          value={institution}
          onChange={(event) => onChange(event.target.value, event.target.name)}
          marginBottom={20}
        />

        <IconSelect
          value={role}
          marginBottom={20}
          label="Role"
          icon="assignment_ind"
          name="role"
          onChange={(event) => onChange(event.target.value, event.target.name)}
        >
          <option value="">Choose</option>
          {roles.map((role) => (
            <option key={role.id} value={role.id}>
              {role.role}
            </option>
          ))}
        </IconSelect>

        <IconSelect
          value={fieldOfStudy}
          marginBottom={20}
          label="Field of Study"
          icon="book"
          name="fieldOfStudy"
          onChange={(event) => onChange(event.target.value, event.target.name)}
        >
          <option value="">Choose</option>

          {studyFields.map((studyField) => (
            <option key={studyField.id} value={studyField.id}>
              {studyField.name}
            </option>
          ))}
        </IconSelect>

        <Button
          onClick={onSave}
          variant="primary"
          isInline
          isLoading={isLoading}
          disabled={name.length < 2 || (whatsappNumber && whatsappNumber.length < 8)}
        >
          Save changes
        </Button>
      </ProfileContainer>
    </Form>
  );
}

GeneralForm.propTypes = Props;
